import SkeletonRow from "@/components/SkeletonRow";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackgroundGradient } from "@/components/ui/background-gradient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebounce } from "@/hooks/useDebounce";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { getInitials } from "@/lib/utils";
import { getListPesertaVerifikasi } from "@/services/getData";
import { DataPendaftaran, DataSiswa } from "@/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { HiOutlineArrowNarrowLeft } from "react-icons/hi";
import {
  HiChevronDoubleLeft,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from "react-icons/hi2";
import { LuListFilter } from "react-icons/lu";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Aset from "./partial/Aset";
import Berkas from "./partial/Berkas";
import Keluarga from "./partial/Keluarga";

const getPeserta = async (nomor_pendaftaran: string) => {
  const response = await api.get(
    `/verifikator/peserta/show/${nomor_pendaftaran}`,
  );
  return response.data?.data;
};

// --- Definisi Konfigurasi Tab ---
const TAB_CONFIG = [
  { id: "keluarga", title: "Keluarga & Tanggungan", index: 1 },
  { id: "aset", title: "Data Aset Kepemilikan", index: 2 },
  { id: "berkas", title: "Dokumen & Berkas", index: 3 },
] as const;

export default function VerifikasiBerkasDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  // Ambil parameter dari URL
  const nomorPendaftaranParams = searchParams.get("nomor_pendaftaran");
  const activeTabParam = searchParams.get("tab") || "keluarga";
  const isVerifikatorUktAll = user?.jenis_user?.includes("verifikator_ukt_all");

  const { data, isPending } = useQuery({
    queryKey: ["peserta", nomorPendaftaranParams],
    queryFn: () => getPeserta(String(nomorPendaftaranParams)),
    enabled: !!nomorPendaftaranParams,
  });

  const dataSiswa: DataSiswa = data?.data_siswa;
  const dataPendaftaran: DataPendaftaran | undefined = data;

  const filterContainerRef = useRef<HTMLDivElement | null>(null);

  const [showFilter, setShowFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );

  // Params untuk fetching list
  const pageParams = searchParams.get("page") || "1";
  const rowsPerPageParams = searchParams.get("rowsPerPage") || "10";
  const searchQueryParams = searchParams.get("search") || "";
  const statusValidasiParams = searchParams.get("statusValidasi") || "";
  const debouncedQuery = useDebounce(searchQuery, 1000);
  const jenisVerifikatorUktParams =
    searchParams.get("jenisVerifikatorUkt") ||
    (isVerifikatorUktAll ? "all" : "biasa");

  const queryClient = useQueryClient();

  const { data: listPeserta, isPending: isPendingPeserta } = useQuery({
    queryKey: [
      "listPesertaVerifikasi",
      pageParams,
      rowsPerPageParams,
      searchQueryParams,
      statusValidasiParams,
      jenisVerifikatorUktParams,
    ],
    queryFn: () =>
      getListPesertaVerifikasi(
        pageParams,
        rowsPerPageParams,
        searchQueryParams,
        statusValidasiParams,
        jenisVerifikatorUktParams,
      ),
  });

  // Handler Ganti Tab (Push ke URL)
  const handleTabChange = (tabId: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", tabId);
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigasi Prev/Next via URL
  const currentTabIndex = TAB_CONFIG.findIndex((t) => t.id === activeTabParam);

  const handlePrevTab = () => {
    if (currentTabIndex > 0)
      handleTabChange(TAB_CONFIG[currentTabIndex - 1].id);
  };

  const handleNextTab = () => {
    if (currentTabIndex < TAB_CONFIG.length - 1)
      handleTabChange(TAB_CONFIG[currentTabIndex + 1].id);
  };

  // Render Konten berdasarkan Tab Aktif
  const renderContent = () => {
    switch (activeTabParam) {
      case "keluarga":
        return (
          <Keluarga
            dataKeluarga={dataPendaftaran?.data_keluarga}
            dataPendaftaran={dataPendaftaran}
            dataSiswa={dataSiswa}
            isPending={isPending}
          />
        );
      case "aset":
        return (
          <Aset dataAset={dataPendaftaran?.data_aset} isPending={isPending} />
        );
      case "berkas":
        return (
          <Berkas
            dataBerkas={dataPendaftaran?.data_berkas}
            isPending={isPending}
            dataSiswa={data?.data_siswa}
            dataKeluarga={dataPendaftaran?.data_keluarga}
          />
        );
      default:
        return null;
    }
  };

  // Menutup filter saat klik di luar container filter
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(event.target as Node)
      ) {
        setShowFilter(false);
      }
    };
    if (showFilter) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilter]);

  // Mengatur Pencarian
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (debouncedQuery) {
      if (params.get("search") !== debouncedQuery) params.delete("page");
      params.set("search", debouncedQuery);
    } else {
      params.delete("search");
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [debouncedQuery, navigate, location.pathname]);

  const handleFilterStatusChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "semua") newParams.delete("statusValidasi");
    else newParams.set("statusValidasi", value);

    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const returnUrl = searchParams.get("returnTo")
    ? decodeURIComponent(searchParams.get("returnTo")!)
    : "/verifikator/verifikasi-berkas";

  return (
    // Dihapus: h-full, min-h-screen, flex-col, flex-1 yang memaksa ukuran
    <div className="relative w-full bg-slate-50 dark:bg-slate-950 pb-20">
      {/* Overlay Blur */}
      <AnimatePresence>
        {showFilter && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-sm"
          />
        )}
      </AnimatePresence>

      {/* HEADER (STICKY ATAS) */}
      <header className="sticky top-0 z-50 w-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-4 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 transition-colors duration-200">
        <div className="flex items-center gap-4 lg:gap-6">
          <Link to={returnUrl}>
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full dark:border-slate-700 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
            >
              <HiOutlineArrowNarrowLeft className="h-5 w-5" />
            </Button>
          </Link>

          <div className="flex items-center gap-3 md:gap-4">
            <Avatar className="w-12 h-12 md:w-14 md:h-14 border-2 border-white dark:border-slate-800 shadow-md">
              <AvatarImage
                src={`${dataSiswa?.path_foto || ""}`}
                alt="foto"
                className="object-cover"
              />
              <AvatarFallback className="bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-bold">
                {getInitials(dataSiswa?.nama_lengkap || "Peserta")}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <h1 className="font-bold text-lg md:text-xl text-slate-900 dark:text-slate-100 leading-tight">
                {dataSiswa?.nama_lengkap || "Memuat data..."}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary text-xs font-semibold px-2 py-0.5 rounded-md">
                  {dataPendaftaran?.nomor_pendaftaran || "-"}
                </span>
                <span className="text-sm text-slate-500 dark:text-slate-400 hidden sm:inline-block">
                  | {dataSiswa?.email || "email@tidak.ada"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* POPOVER TRIGGER */}
        <div
          className="relative flex items-center justify-end z-50"
          ref={filterContainerRef}
        >
          <div className="rounded-full overflow-hidden">
            <BackgroundGradient animate={false}>
              <Button
                className="flex items-center gap-2 rounded-full px-5 bg-white text-slate-900 hover:bg-slate-50 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 shadow-sm"
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: ["listPesertaVerifikasi"],
                  });
                  setShowFilter(!showFilter);
                }}
              >
                <LuListFilter className="h-4 w-4 text-primary" />
                <span className="font-medium">Pilih Peserta Lain</span>
              </Button>
            </BackgroundGradient>
          </div>

          {/* POPOVER CONTENT */}
          <AnimatePresence>
            {showFilter && (
              <motion.div
                initial={{ opacity: 0, y: 15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute top-[120%] right-0 w-[95vw] sm:w-[450px] lg:w-[500px] rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col"
              >
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80">
                  <Tabs
                    value={statusValidasiParams || "semua"}
                    onValueChange={handleFilterStatusChange}
                    className="w-full mb-3"
                  >
                    <TabsList className="grid w-full grid-cols-3 rounded-xl bg-slate-200/50 dark:bg-slate-800">
                      <TabsTrigger
                        value="semua"
                        className="rounded-lg text-xs data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700"
                      >
                        Semua
                      </TabsTrigger>
                      <TabsTrigger
                        value="belum-valid"
                        className="rounded-lg text-xs data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700"
                      >
                        Belum
                      </TabsTrigger>
                      <TabsTrigger
                        value="valid"
                        className="rounded-lg text-xs data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700"
                      >
                        Sudah
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Input
                    placeholder="Cari nama atau nomor..."
                    className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 h-10 focus-visible:ring-primary"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="flex-1 overflow-y-auto max-h-[50vh] p-2 custom-scrollbar bg-white dark:bg-slate-900">
                  {isPendingPeserta ? (
                    <div className="space-y-2 p-2">
                      {Array(5)
                        .fill(0)
                        .map((_, i) => (
                          <SkeletonRow key={i} />
                        ))}
                    </div>
                  ) : listPeserta?.data?.length > 0 ? (
                    <ul className="space-y-1">
                      {listPeserta.data.map(
                        (item: DataPendaftaran, index: number) => {
                          const isActive =
                            nomorPendaftaranParams === item.nomor_pendaftaran;
                          return (
                            <li
                              key={index}
                              className={`p-3 rounded-xl flex items-center justify-between border transition-all cursor-pointer ${
                                isActive
                                  ? "bg-primary/5 border-primary/20 dark:bg-primary/10 dark:border-primary/20"
                                  : "border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                              }`}
                              onClick={() => {
                                const newParams = new URLSearchParams(
                                  searchParams,
                                );
                                newParams.set("id", `${item.id_data_siswa}`);
                                newParams.set(
                                  "nomor_pendaftaran",
                                  `${item.nomor_pendaftaran}`,
                                );
                                newParams.set("tab", "keluarga");
                                setSearchParams(newParams);
                                setShowFilter(false);
                              }}
                            >
                              <div className="flex items-center gap-3 overflow-hidden">
                                <Avatar className="h-10 w-10 border dark:border-slate-700 shrink-0">
                                  <AvatarFallback
                                    className={`text-xs font-bold ${
                                      isActive
                                        ? "bg-primary/20 text-primary"
                                        : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                                    }`}
                                  >
                                    {getInitials(
                                      item.data_siswa?.nama_lengkap || "P",
                                    )}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col overflow-hidden">
                                  <span
                                    className={`text-sm truncate ${
                                      isActive
                                        ? "font-bold text-primary"
                                        : "font-semibold text-slate-800 dark:text-slate-200"
                                    }`}
                                  >
                                    {item.data_siswa?.nama_lengkap}
                                  </span>
                                  <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                                    {item.nomor_pendaftaran}
                                  </span>
                                </div>
                              </div>
                              {isActive && (
                                <div className="h-2.5 w-2.5 rounded-full bg-primary shadow-md shadow-primary/50 shrink-0" />
                              )}
                            </li>
                          );
                        },
                      )}
                    </ul>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-slate-500 dark:text-slate-400">
                      <LuListFilter className="h-8 w-8 mb-2 opacity-20" />
                      <span className="text-sm">
                        Data peserta tidak ditemukan.
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950">
                  <span className="text-xs text-slate-500 font-medium">
                    Hal. {listPeserta?.current_page || 1} /{" "}
                    {listPeserta?.last_page || 1}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={listPeserta?.current_page == 1}
                      onClick={() => {
                        const p = new URLSearchParams(searchParams);
                        p.set("page", "1");
                        setSearchParams(p);
                      }}
                    >
                      <HiChevronDoubleLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={listPeserta?.current_page == 1}
                      onClick={() => {
                        const p = new URLSearchParams(searchParams);
                        p.set("page", `${listPeserta?.current_page - 1}`);
                        setSearchParams(p);
                      }}
                    >
                      <HiOutlineChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={
                        listPeserta?.current_page === listPeserta?.last_page
                      }
                      onClick={() => {
                        const p = new URLSearchParams(searchParams);
                        p.set("page", `${listPeserta?.current_page + 1}`);
                        setSearchParams(p);
                      }}
                    >
                      <HiOutlineChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* NAVIGASI TABS */}
      <div className="w-full px-4 mt-6 z-20">
        {/* Pembungkus utama dengan style Segmented Control */}
        {/* Ditambahkan styling scrollbar tersembunyi agar rapi di mobile */}
        <div className="flex p-1.5 space-x-1 overflow-x-auto bg-slate-100/80 dark:bg-slate-800/40 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
          {TAB_CONFIG.map((tab) => {
            const isActive = activeTabParam === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`relative flex items-center justify-center px-4 py-3 flex-1 min-w-[240px] rounded-xl outline-none transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  isActive
                    ? "text-primary" // Warna teks saat aktif
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50"
                }`}
              >
                {/* Animated Background Pill (Framer Motion)
            Hanya dirender pada tab yang aktif, akan meluncur antar tab 
          */}
                {isActive && (
                  <motion.div
                    layoutId="activeTabPill"
                    className="absolute inset-0 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200/60 dark:border-slate-700/50"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}

                {/* Konten Tab (Berada di atas layer animasi) */}
                <div className="relative z-10 flex items-center gap-3">
                  {/* Lingkaran Nomor / Index */}
                  <div
                    className={`flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold transition-all duration-300 ${
                      isActive
                        ? "bg-primary/10 text-primary dark:bg-primary/20"
                        : "bg-slate-200/70 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                    }`}
                  >
                    {tab.index}
                  </div>

                  {/* Judul Tab */}
                  <span className="font-semibold text-sm tracking-tight whitespace-nowrap">
                    {tab.title}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* KONTEN UTAMA */}
      <div className="w-full px-4 mt-6 mb-6 z-20">{renderContent()}</div>

      {/* ACTION BAR (STICKY BAWAH) */}
      <div className="sticky bottom-0 z-30 w-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 p-4 px-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] transition-colors duration-200">
        <div className="w-full flex items-center justify-between gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={handlePrevTab}
            disabled={currentTabIndex === 0}
            className="flex items-center gap-2 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 disabled:opacity-40 min-w-[140px]"
          >
            <HiOutlineChevronLeft className="h-5 w-5" />{" "}
            <span className="hidden sm:inline">Sebelumnya</span>
          </Button>

          <div className="hidden md:flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400">
            Langkah {currentTabIndex + 1} dari {TAB_CONFIG.length}
          </div>

          {currentTabIndex < TAB_CONFIG.length - 1 ? (
            <Button
              size="lg"
              onClick={handleNextTab}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground min-w-[140px] shadow-sm"
            >
              <span className="hidden sm:inline">Selanjutnya</span>{" "}
              <HiOutlineChevronRight className="h-5 w-5" />
            </Button>
          ) : (
            <Button
              size="lg"
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white min-w-[140px] shadow-sm"
              onClick={() => {
                navigate("/verifikator/verifikasi-berkas");
              }}
            >
              Selesai Verifikasi
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
